import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Table, message } from "antd";
import { getTablePagination } from "../../../../utils/tablePagination";
import { getOpFacilityServicesApi } from "../../../../services/opservices.services";
import { getCookie } from "../../../../helpers/localStorage";
import { UserContext } from "../../../../context/UserContext";
import { FacilityID } from "../../../../constants/defaultKeys";
import { formatToINR } from "../../../../helpers/utility";
import AssignOpServiceModal from "./AssignOpServiceModal";
import { useSearchParams, useNavigate } from "react-router-dom";
import { UiButton, UiDataTable } from "../../../ui";
import useQuery from "../../../../hooks/useQuery";
import {
  parsePageParam,
  updateUrlSearchParams,
} from "../../../../helpers/urlFilters";
import useIsMobile from "../../../../hooks/useIsMobile";
import usePageFillTableScroll from "../../../../hooks/usePageFillTableScroll";

const ServicesOpTable = ({
  searchQuery = "",
  categoryFilter = null,
  categories = [],
  onCountChange,
}) => {
  const { userDetails } = useContext(UserContext);
  const navigate = useNavigate();
  const queryParams = useQuery();
  const isMobile = useIsMobile();
  const tableWrapRef = useRef(null);
  const tableScroll = usePageFillTableScroll(tableWrapRef, isMobile, "services");
  const [services, setServices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const page = parsePageParam(queryParams, 1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams] = useSearchParams();
  const admissionId = searchParams.get("admission");
  const refreshServices = () => setRefresh((prev) => !prev);

  useEffect(() => {
    if (!admissionId) return;

    const facilityId = Number(getCookie(FacilityID)) || userDetails?.currFacility;
    const params = {
      op_admission: admissionId,
      page: categoryFilter ? 1 : page,
      limit: categoryFilter ? 1000 : pageSize,
    };

    if (searchQuery) params.search = searchQuery;
    if (categoryFilter) params.category_id = categoryFilter;

    setLoading(true);
    getOpFacilityServicesApi(facilityId, params)
      .then((res) => {
        const data = res?.data;
        if (data?.results) {
          setServices(data.results);
          setTotalCount(data.count || 0);
        } else {
          setServices([]);
          setTotalCount(0);
        }
      })
      .catch((err) => {
        console.error(err);
        message.error("Failed to load services");
      })
      .finally(() => setLoading(false));
  }, [
    refresh,
    searchQuery,
    categoryFilter,
    page,
    pageSize,
    admissionId,
    userDetails?.currFacility,
  ]);

  const tableData = useMemo(() => {
    if (!categoryFilter) return services;
    const categoryName = categories.find((item) => item.id === categoryFilter)
      ?.name;
    return services.filter(
      (item) =>
        Number(item?.category_id) === Number(categoryFilter) ||
        Number(item?.category?.id) === Number(categoryFilter) ||
        (categoryName && item?.category_name === categoryName)
    );
  }, [services, categoryFilter, categories]);

  const displayCount = categoryFilter ? tableData.length : totalCount;

  useEffect(() => {
    onCountChange?.(displayCount);
  }, [displayCount, onCountChange]);

  const columns = [
    {
      title: "Service Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      ellipsis: true,
      onCell: () => ({ style: { maxWidth: 200 } }),
    },
    {
      title: "Category",
      dataIndex: "category_name",
      key: "category",
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Price",
      dataIndex: "cash_price",
      key: "cash_price",
      width: 120,
      render: (value) => formatToINR(value),
    },
    {
      title: "Action",
      key: "action",
      width: 110,
      align: "center",
      render: (_, record) => (
        <UiButton
          variant="secondary"
          size="sm"
          onClick={() => {
            setOpen(true);
            setEditData(record);
          }}
        >
          Assign
        </UiButton>
      ),
    },
  ];

  return (
    <>
      <UiDataTable showSearch={false} showHeader={false} shellClassName="ui-table-fill-shell">
        <div ref={tableWrapRef}>
          <Table
            className="no-vertical-borders"
            dataSource={tableData}
            columns={columns}
            pagination={
              categoryFilter
                ? false
                : getTablePagination({
                    current: page,
                    pageSize,
                    total: totalCount ?? 0,
                    onChange: (p) => updateUrlSearchParams(navigate, { page: p }),
                  })
            }
            loading={loading}
            rowKey="id"
            scroll={isMobile ? { x: "max-content" } : tableScroll}
          />
        </div>
      </UiDataTable>
      {open && (
        <AssignOpServiceModal
          refresh={refreshServices}
          open={open}
          setOpen={setOpen}
          editData={editData}
          patientId={admissionId}
        />
      )}
    </>
  );
};

export default ServicesOpTable;
